import { Component, OnInit, OnDestroy, Input, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView } from 'angular-calendar';
import { AdminService } from '../../service/admin.service';
import { User } from '../../model/user';
import { Event } from '../../model/event';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '../../service/user.service';
import { EventsService } from '../../service/events.service';
import { SubmissionsService } from '../../service/submissions.service';
import { SHOW_ALL_PAGINATION_OPTIONS } from '../../model/pagination.options';
import { SubmissionFilter } from 'src/app/model/submission.filter';
import { Submission, Review, SubmissionStatus } from 'src/app/model/paper';
import { ReviewsService } from '../../service/reviews.service';
import { UserEventRole } from '../../enum/user.event.role';
import { map } from 'rxjs/operators';
import { zip, Observable, Subscription, of, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { AssignReview } from 'src/app/model/assign.review';
import { InfoService } from 'src/app/service/info.service';
import { NotificationService } from 'src/app/service/notification.service';
import { SelectionClipboardService } from 'src/app/service/selection-clipboard.service';
import { AuthService } from 'src/app/service/auth.service';
import { slideOutAnimation } from 'src/app/animations/slideInOut';

//import { Session } from 'protractor';
import { Session } from 'src/app/model/session';
import { TimezoneService } from 'src/app/service/timezone.service';
import { ReviewStatus } from 'src/app/enum/review.status';

interface ExtendedCalendarEventSessions extends CalendarEvent {
  event: any;
  title: string;
  subtitle: string;
  url: string;
  room: string;
  submissions: Array<Submission>;
}

class EventRole {
  chair = false;
  committee = false;
  publication_chair = false;
  reviewer = false;

  constructor(
    public event: Event
  ) { }

  setRole(role: UserEventRole) {
    switch (role) {
      case UserEventRole.CHAIR:
        this.chair = true;
        break;
      case UserEventRole.COMMITTEE:
        this.committee = true;
        break;
      case UserEventRole.PUBLICATION_CHAIR:
        this.publication_chair = true;
        break;
      case UserEventRole.REVIEWER:
        this.reviewer = true;
        break;
      default:
        break;
    }
  }
}

const BUTTONS = {
  down: 'fa-chevron-down',
  up: 'fa-chevron-up'
};

class RequestedSubmission extends Submission {
  reviewers: number;
  hasUserReview: boolean;

  assignToUser = false;
}

@Component({
  selector: 'app-event-user-view',
  templateUrl: './event-user-view.component.html',
  styleUrls: ['./event-user-view.component.scss'],
  animations: [
    slideOutAnimation
  ]
})
export class EventUserViewComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() event: Event;
  @Input() user: User;

  @Input() showRequested = false;
  @Input() showRelatedTo = false;

  isAdminOrChair = false;

  submissionsRequested: Array<RequestedSubmission>;
  submissionsAuthored: Array<Submission>;
  sessionSubmissions: Array<Submission>;
  assignedToOthers: Array<Review>;
  assignedToUser: Array<Review>;
  assignedToUserAndOthers: Array<Review>;
  relatedToEvent: Array<EventRole>;
  isComittee: Boolean;
  reviewConfig: EventReviewConfiguration;

  eventReviews: Review[];

  toggleButtons = {
    submissionsRequested: BUTTONS.down,
    submissionsAuthored: BUTTONS.down,
    assignedToOthers: BUTTONS.down,
    SessionSubmissions: BUTTONS.down,
    assignedToUser: BUTTONS.down,
    assignedToUserAndOthers: BUTTONS.down,
    relatedToEvent: BUTTONS.down
  };

  loading = {
    submissionsRequested: false,
    submissionsAuthored: false,
    assignedToOthers: false,
    SessionSubmissions: false,
    assignedToUser: false,
    assignedToUserAndOthers: false,
    relatedToEvent: false,
    relatedToEventIndex: 0
  };

  assignDateForm: FormGroup;
  assignDateLink;
  subscriptions: Subscription[] = [];

  // Sets de seleção separados por seção (submissões do autor, revisões
  // atribuídas, submissões em sessão). Cada Set conta pra barra flutuante.
  public selectedSubmissionAuthoredIds = new Set<number>();
  public selectedReviewIds = new Set<number>();
  public selectedSessionSubmissionIds = new Set<number>();

  constructor(
    private eventsService: EventsService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private submissionsService: SubmissionsService,
    private reviewsService: ReviewsService,
    private infoService: InfoService,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private authService: AuthService,
    private tzService: TimezoneService,
    private elementRef: ElementRef,
    private selectionClipboard: SelectionClipboardService,
  ) { }

  toggleSubmissionAuthored(id: number, checked: boolean): void {
    if (checked) { this.selectedSubmissionAuthoredIds.add(id); }
    else { this.selectedSubmissionAuthoredIds.delete(id); }
  }
  toggleReviewAssigned(id: number, checked: boolean): void {
    if (checked) { this.selectedReviewIds.add(id); } else { this.selectedReviewIds.delete(id); }
  }
  toggleSessionSubmission(id: number, checked: boolean): void {
    if (checked) { this.selectedSessionSubmissionIds.add(id); }
    else { this.selectedSessionSubmissionIds.delete(id); }
  }
  clearSelection(): void {
    this.selectedSubmissionAuthoredIds.clear();
    this.selectedReviewIds.clear();
    this.selectedSessionSubmissionIds.clear();
  }
  get selectionCount(): number {
    return this.selectedSubmissionAuthoredIds.size
         + this.selectedReviewIds.size
         + this.selectedSessionSubmissionIds.size;
  }
  async copySelection(): Promise<void> {
    // Ordem visual da página: submissões do autor → revisões atribuídas →
    // submissões em sessão. Constrói a lista de items na MESMA ordem que
    // aparecem na tela pra que o copy preserve a sequência.
    const items: Array<{ type: 'submission' | 'review', data: any }> = [];
    for (const s of (this as any).submissionsAuthored || []) {
      if (this.selectedSubmissionAuthoredIds.has(s.id)) {
        items.push({ type: 'submission', data: s });
      }
    }
    for (const r of (this as any).assignedToUserAndOthers || []) {
      if (this.selectedReviewIds.has(r.id)) {
        items.push({ type: 'review', data: r });
      }
    }
    for (const s of (this as any).sessionSubmissions || []) {
      if (this.selectedSessionSubmissionIds.has(s.id)) {
        items.push({ type: 'submission', data: s });
      }
    }
    // notificação removida temporariamente: o MatSnackBar adiciona o
    // cdk-overlay-container ao body e em alguns destinos a scrollbar do
    // window oscila momentaneamente, gerando um "pulo" na página depois
    // do copy. Sem o snackbar, o copy não dispara nenhum efeito visual
    // externo ao próprio clipboard.
    await this.selectionClipboard.copyOrdered(items);
  }

  ngAfterViewInit(): void {
    this.scheduleColEqualize();
  }

  @HostListener('window:resize')
  onWindowResizeForCols(): void {
    this.equalizeSubmissionCardColumns();
  }

  // Agenda algumas passadas do equalize — o conteúdo (fontes de ícone, file
  // pills) pode assentar depois do primeiro paint e mudar as larguras.
  private scheduleColEqualize(): void {
    [0, 300, 800].forEach(delay => setTimeout(() => this.equalizeSubmissionCardColumns(), delay));
  }

  // Iguala a largura das colunas laterais (ID, sessão, arquivos) entre TODOS os
  // cards de submissão do componente, alinhando-as card a card (a principal
  // `.snc-main`, flex:1, absorve a folga). Mesma lógica do equalize de "Minhas
  // submissões" (home/submissions), estendida para a coluna de sessão.
  private equalizeSubmissionCardColumns(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    ['.snc-id-col', '.snc-session-col', '.snc-files-aside'].forEach(selector => {
      const els = Array.from(host.querySelectorAll(selector)) as HTMLElement[];
      if (!els.length) { return; }
      els.forEach(el => { el.style.minWidth = ''; });
      const max = els.reduce((m, el) => Math.max(m, el.offsetWidth), 0);
      els.forEach(el => { el.style.minWidth = max + 'px'; });
    });
  }

  ngOnInit(): void {
    this.eventsService.getUserIsUserEvent(this.event.id, UserEventRole.COMMITTEE, this.user.id).subscribe(bool => {
      this.isComittee = bool;
    });

    this.authService.userIsAdminOrChair(this.event.id).subscribe(isAdminOrChair => {
      this.isAdminOrChair = isAdminOrChair;

      if (this.isAdminOrChair) {
        this.assignDateLink = {
          label: 'forms.buttons.assign-review-date',
          route: `/admin/${this.event.id}/reviews/configuration`,
          align: 'start'
        };

        if (this.showRequested) {
          // "Submissões requisitadas" é exclusiva da interface do TPC Chair
          // (`showRequested`): só busca aqui. No público (autor/revisor) a seção
          // não é exibida, então não há fetch.
          this.getSubmissionsRequested();
        }
      } else {
        this.toggleButtons.assignedToOthers = BUTTONS.up;
        this.getAssignedToOthers();
      }

      // getAssignedToUser/getAssignedToUserAndOthers dependem de isAdminOrChair para decidir
      // se revisões com status ASSIGNED entram na lista (ver getEventReviews). Só disparam
      // aqui, depois desse valor resolver, para não perder a corrida contra essa checagem
      // assíncrona e acabar escondendo revisões ASSIGNED do chair.
      this.toggleButtons.assignedToUser = BUTTONS.up;
      this.getAssignedToUser();
      this.getAssignedToUserAndOthers();
    });

    this.reviewConfigurationService.getReviewsConfiguration(this.event.id).subscribe(configuration => {
      this.reviewConfig = configuration;
      this.assignDateForm = this.fb.group({
        dueAt: [configuration.conferenceDateDue ?.toDate()],
        timeZone: [this.tzService.browserTimeZone]
      });
    });

    this.toggleButtons.submissionsAuthored = BUTTONS.up;
    this.getSubmissionsAuthored();
    this.getSessionSubmissions();
    // Listas sempre visíveis (sem toggle): carrega "Eventos relacionados" no init
    // quando a seção é exibida — antes só carregava ao expandir.
    if (this.showRelatedTo) {
      this.getRelationToEvents();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  public clearComponent() {
    this.submissionsRequested = undefined;
    this.submissionsAuthored = undefined;
    this.relatedToEvent = undefined;
    this.assignedToUser = undefined;
    this.assignedToOthers = undefined;
    this.assignedToUserAndOthers = undefined;

    this.loading = {
      submissionsRequested: false,
      submissionsAuthored: false,
      SessionSubmissions: false,
      assignedToOthers: false,
      assignedToUser: false,
      assignedToUserAndOthers: false,
      relatedToEvent: false,
      relatedToEventIndex: 0
    };

    this.toggleButtons = {
      submissionsRequested: BUTTONS.down,
      submissionsAuthored: BUTTONS.down,
      SessionSubmissions: BUTTONS.down,
      assignedToOthers: BUTTONS.down,
      assignedToUserAndOthers: BUTTONS.down,
      assignedToUser: BUTTONS.down,
      relatedToEvent: BUTTONS.down
    };
  }

  getSubmissionsRequested() {
    if (!this.submissionsRequested) {
      this.loading.submissionsRequested = true;

      this.submissionsService.getSubmissionsToClaim(this.event.id, this.user.id).subscribe(submissions => {
        this.submissionsRequested = <RequestedSubmission[]>submissions.filter(s => (s as any).isClaimedByUser);

        if (this.submissionsRequested.length === 0) {
          this.loading.submissionsRequested = false;
          this.toggleButtons.submissionsRequested = BUTTONS.up;

          this.submissionsRequested = [];
          return;
        }

        forkJoin(this.submissionsRequested.map(s => this.reviewsService.getReviewsBySubmissionId(s.id))).subscribe(submissionReviews => {
          submissionReviews.forEach((reviews, index) => {
            const submission = this.submissionsRequested[index];
            submission.reviewers = reviews.length;
            submission.hasUserReview = !!reviews.find(review => review.userId === this.user.id);
            submission.status = SubmissionStatus.ACTIVE;
          });

          this.loading.submissionsRequested = false;
          this.toggleButtons.submissionsRequested = BUTTONS.up;
        });
      });
    } else {
      this.submissionsRequested = undefined;
      this.toggleButtons.submissionsRequested = BUTTONS.down;
    }
  }

  assignSubmissions() {
    const submissions = this.submissionsRequested.filter(s => s.assignToUser);
    const dueAt = this.assignDateForm.value.dueAt;

    const reviews = submissions.map(submission => {
      const review = new AssignReview();

      review.submission = submission.id;
      review.assignedBy = this.infoService.user.id;
      review.user = this.user.id;
      review.dueAt = dueAt;

      return review;
    });

    forkJoin(reviews.map(r => this.reviewsService.createReview(r))).subscribe(createdReviews => {
      if (createdReviews ?.length > 0) {
        this.notificationService.notify('messages.assigned-user-review', { params: { user: createdReviews[0].userId } });

        if (this.assignedToUser ?.length > 0) {
          this.assignedToUser.push(...createdReviews);
        }
        this.submissionsRequested = undefined;
        this.getSubmissionsRequested();
      }
    });
  }

  getSubmissionsAuthored() {
    if (!this.submissionsAuthored) {
      this.loading.submissionsAuthored = true;
      this.submissionsService.getUserIDSubmissionsFromEvent(this.event.id, this.user.id).subscribe(p => {
        const submissions = p.items as Submission[];
        this.submissionsAuthored = submissions.map(e => {
          e.event = this.event;
          e.track = this.event.tracks.find(t => e.trackID === t.id);
          return e;
        });
        this.loading.submissionsAuthored = false;
        this.toggleButtons.submissionsAuthored = BUTTONS.up;
        this.scheduleColEqualize();
      });
    } else {
      this.submissionsAuthored = undefined;
      this.toggleButtons.submissionsAuthored = BUTTONS.down;
    }
  }

  getSessionSubmissions() {
    if (!this.sessionSubmissions) {
      this.loading.SessionSubmissions = true;
      this.submissionsService.getSessionsSubmissionsBySessionChair(this.event.id, this.user.id).subscribe(p => {
        const submissions = p.items as Submission[];
        this.sessionSubmissions = submissions.map(e => {
          e.event = this.event;
          e.track = this.event.tracks.find(t => e.trackID === t.id);
          return e;
        });
        this.sessionSubmissions = this.sessionSubmissions.sort(function (a, b):number {
          if((a.session as unknown as Session).id < (b.session as unknown as Session).id){
            return -1;
          }else if((a.session as unknown as Session).id === (b.session as unknown as Session).id){
            if(a.id < b.id){
              return -1;
            }else{
              return +1;
            }
          }else{
            return +1;
          }
        });
        this.loading.SessionSubmissions = false;
        this.toggleButtons.SessionSubmissions = BUTTONS.up;
        this.scheduleColEqualize();
      });
    } else {
      this.sessionSubmissions = undefined;
      this.toggleButtons.SessionSubmissions = BUTTONS.down;
    }
  }


  public updateSubmission(updatedSubmission: Submission): void {
    if (this.submissionsAuthored) {
      this.submissionsAuthored.splice(
        this.submissionsAuthored.findIndex(sub => sub.id === updatedSubmission.id),
        1, updatedSubmission
      );
    }
  }

  getEventReviews(): Observable<Review[]> {
    if (this.eventReviews) {
      // Reaplica o mesmo filtro do fetch original: sem isso, chamadas que caem no cache
      // devolvem a lista crua (sem excluir ASSIGNED) mesmo para quem não é chair.
      return of(this.isAdminOrChair ? this.eventReviews
        : this.eventReviews.filter(re => re.status != ReviewStatus.ASSIGNED));
    } else {
      return this.reviewsService.getReviewsAssignedByEventByID(this.event.id, this.user.id)
        .pipe(map(r => {
          this.eventReviews = r;

          return this.isAdminOrChair ? this.eventReviews 
          : this.eventReviews.filter(re => re.status != ReviewStatus.ASSIGNED);
        }));
    }
  }

  getAssignedToUser() {
    if (!this.assignedToUser) {
      this.loading.assignedToUser = true;
      this.subscriptions.push(this.getEventReviews().subscribe(reviews => {
        this.assignedToUser = reviews
          .filter(r => r.user instanceof User ?
            r.user.id === this.user.id :
            r.user === this.user.id
          )
          .map(e => {
            e.submission.track = this.event.tracks.find(t => e.submission.trackID === t.id);
            return e;
          });

        this.loading.assignedToUser = false;
        this.toggleButtons.assignedToUser = BUTTONS.up;
      }));
    } else {
      this.assignedToUser = undefined;
      this.toggleButtons.assignedToUser = BUTTONS.down;
    }
  }

  getAssignedToUserAndOthers() {
    if (!this.assignedToUserAndOthers) {
      this.loading.assignedToUserAndOthers = true;
      this.subscriptions.push(this.getEventReviews().subscribe(reviews => {
        this.assignedToUserAndOthers = reviews
          .filter(r => {
            const isUserMatch = r.user instanceof User
              ? r.user.id === this.user.id
              : r.user === this.user.id;

            const isAssignedByMatch = r.assignedBy instanceof User
              ? r.assignedBy.id === this.user.id
              : r.assignedBy === this.user.id;

            return (isAssignedByMatch && !r.isFirstAssign) || isUserMatch;
          }
          )
          .map(e => {
            e.submission.track = this.event.tracks.find(t => e.submission.trackID === t.id);
            return e;
          })
          // Mesma política de ordenação da página "Minhas revisões" (home/reviews):
          // 1) revisões próprias (sou o revisor) antes das delegadas;
          // 2) por id da submissão (ascendente).
          .sort((a, b) => {
            const isMine = (r: any) => (r.user instanceof User ? r.user.id : r.user) === this.user.id;
            const aMine = isMine(a) ? 0 : 1;
            const bMine = isMine(b) ? 0 : 1;
            if (aMine !== bMine) { return aMine - bMine; }
            return (a.submission?.id ?? 0) - (b.submission?.id ?? 0);
          });

        this.loading.assignedToUserAndOthers = false;
        this.toggleButtons.assignedToUserAndOthers = BUTTONS.up;
      }));
    } else {
      this.assignedToUserAndOthers = undefined;
      this.toggleButtons.assignedToUserAndOthers = BUTTONS.down;
    }
  }

  getAssignedToOthers() {
    if (!this.assignedToOthers) {
      this.loading.assignedToOthers = true;

      this.subscriptions.push(this.getEventReviews().subscribe(reviews => {
        this.assignedToOthers = reviews
          .filter(r => (r.assignedBy instanceof User ?
            r.assignedBy.id === this.user.id :
            r.assignedBy === this.user.id) && !(r.user instanceof User ?
            r.user.id === this.user.id :
            r.user === this.user.id)
          )
          .map(e => {
            e.submission.track = this.event.tracks.find(t => e.submission.trackID === t.id);
            return e;
          });

        this.loading.assignedToOthers = false;
        this.toggleButtons.assignedToOthers = BUTTONS.up;
      }));
    } else {
      this.assignedToOthers = undefined;
      this.toggleButtons.assignedToOthers = BUTTONS.down;
    }
  }

  getRelationToEvents() {
    if (!this.relatedToEvent) {
      this.relatedToEvent = [];
      this.loading.relatedToEvent = true;
      this.loading.relatedToEventIndex = 0;

      this.eventsService.getEventByUserRole(this.user.id, UserEventRole.CHAIR, SHOW_ALL_PAGINATION_OPTIONS)
        .pipe(map(e => e.items))
        .subscribe(events => this.setRolesInEventRoles(events, UserEventRole.CHAIR));

      this.eventsService.getEventByUserRole(this.user.id, UserEventRole.COMMITTEE, SHOW_ALL_PAGINATION_OPTIONS)
        .pipe(map(e => e.items))
        .subscribe(events => this.setRolesInEventRoles(events, UserEventRole.COMMITTEE));

      this.eventsService.getEventByUserRole(this.user.id, UserEventRole.PUBLICATION_CHAIR, SHOW_ALL_PAGINATION_OPTIONS)
        .pipe(map(e => e.items))
        .subscribe(events => this.setRolesInEventRoles(events, UserEventRole.PUBLICATION_CHAIR));

      this.reviewsService.getUserReviews(SHOW_ALL_PAGINATION_OPTIONS, new SubmissionFilter(), this.user.id)
        .pipe(map(e => e.items))
        .subscribe(reviews => {
          const trackList = [...new Set(reviews.map(r => r.submission.trackID))];

          if (trackList.length === 0) {
            // User has no reviews in any event.
            this.setRolesInEventRoles([], UserEventRole.REVIEWER);
          } else {
            zip(...trackList.map(track => this.eventsService.getEventByTrack(track)))
              .subscribe(events => this.setRolesInEventRoles(events, UserEventRole.REVIEWER));
          }

        });
    } else {
      this.relatedToEvent = undefined;
      this.toggleButtons.relatedToEvent = BUTTONS.down;
    }
  }

  setRolesInEventRoles(events: Event[], role: UserEventRole) {
    events.forEach(event => {
      let found = this.relatedToEvent.find(e => e.event.id === event.id);
      if (!found) {
        found = new EventRole(event);
        this.relatedToEvent.push(found);
      }

      found.setRole(role);
    });

    this.loading.relatedToEventIndex++;
    if (this.loading.relatedToEventIndex > 3) {
      this.loading.relatedToEvent = false;
      this.toggleButtons.relatedToEvent = BUTTONS.up;
    }
  }

  getStatusClass(submission): String {
    let statusClass:String;
    statusClass = submission.status.toLowerCase();
    return statusClass;
  }
}